import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { FilePlus, Calendar as CalendarIcon, Send, Sparkles, Wifi, Cable, Signal, ChevronsUpDown, Check, Plus } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function SearchableSelect({ value, onChange, options, placeholder, testId, allowCreate = true }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const exists = trimmed && options.some(o => o.toLowerCase() === trimmed.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testId}
          className={`dolphin-input justify-between font-normal ${!value && "text-slate-400"}`}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type to add…"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>No match — type a name to add a new one.</CommandEmpty>
            {allowCreate && trimmed && !exists && (
              <CommandGroup heading="Add new">
                <CommandItem
                  value={`__create__${trimmed}`}
                  data-testid={`${testId}-create`}
                  onSelect={() => {
                    onChange(trimmed);
                    setQuery("");
                    setOpen(false);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4 text-emerald-500" />
                  Use "{trimmed}"
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {options.map(opt => (
                <CommandItem
                  key={opt}
                  value={opt}
                  data-testid={`${testId}-option-${opt}`}
                  onSelect={() => {
                    onChange(opt);
                    setQuery("");
                    setOpen(false);
                  }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === opt ? "opacity-100 text-[#006994]" : "opacity-0"}`} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

import { CATEGORY_TREE, subcategoriesFor } from "@/constants/categories";

const initialFormState = {
  date: null,
  dd_code: "",
  venue_name: "",
  store_name: "",
  address: "",
  pa: "",
  fa: "",
  installer: "",
  stick_number: "",
  dolphin_manager: "",
  category: "",
  parent_category: "",
  detailed_comments: "",
  network_type: "",
  wifi_name: "",
  wifi_password: "",
  wifi_autoconnect: "",
  latest_apk: "",
  kiosk_mode: "",
  contact_number: "",
  owner_name: "",
  owner_email: ""
};

export default function NewLogEntry() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [prefilledFromDD, setPrefilledFromDD] = useState(false);
  const [lookupStatus, setLookupStatus] = useState("idle");
  const [installerOptions, setInstallerOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/filters`, { withCredentials: true });
        if (cancelled) return;
        setInstallerOptions(res.data?.installers || []);
        setManagerOptions(res.data?.dolphin_managers || []);
      } catch (err) {
        console.error("Error loading installer/manager options:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const ddCode = searchParams.get('dd_code');
    const venueName = searchParams.get('venue_name');
    const storeName = searchParams.get('store_name');
    const address = searchParams.get('address');
    const pa = searchParams.get('pa');
    const fa = searchParams.get('fa');

    if (ddCode) {
      setForm(prev => ({
        ...prev,
        dd_code: ddCode || '',
        venue_name: venueName || '',
        store_name: storeName || '',
        address: address || '',
        pa: pa || '',
        fa: fa || ''
      }));
      setPrefilledFromDD(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const code = (form.dd_code || "").trim();
    if (!code || code.length < 3) {
      setLookupStatus("idle");
      return;
    }

    let cancelled = false;
    setLookupStatus("loading");

    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/device-lookup/${encodeURIComponent(code)}`, {
          withCredentials: true,
        });
        if (cancelled) return;
        const d = res.data || {};
        setForm(prev => ({
          ...prev,
          venue_name: prev.venue_name || d.venue_name || "",
          store_name: prev.store_name || d.store_name || "",
          address: prev.address || d.address || "",
          pa: prev.pa || d.pa || "",
          fa: prev.fa || d.fa || "",
          stick_number: prev.stick_number || d.stick_number || "",
          owner_name: prev.owner_name || d.owner_name || "",
          contact_number: prev.contact_number || d.contact_number || "",
          owner_email: prev.owner_email || d.owner_email || "",
        }));
        setLookupStatus("found");
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setLookupStatus("not_found");
        } else {
          console.error("Device lookup error:", err);
          setLookupStatus("idle");
        }
      }
    }, 450);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.dd_code]);

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const required = ['date', 'dd_code', 'venue_name', 'store_name', 'address', 'pa', 'installer', 'stick_number', 'dolphin_manager', 'category', 'network_type', 'latest_apk', 'kiosk_mode'];
    const missing = required.filter(field => !form[field]);

    if (missing.length > 0) {
      toast.error(`Please fill in all required fields`);
      return false;
    }

    if (form.network_type === "wifi") {
      if (!form.wifi_autoconnect) {
        toast.error("Please select WiFi Autoconnect option");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { parent_category: _ignored, ...rest } = form;
      const payload = {
        ...rest,
        date: format(form.date, "yyyy-MM-dd"),
        detailed_comments: form.detailed_comments || "",
        wifi_name: form.wifi_name || "",
        wifi_password: form.wifi_password || ""
      };

      await axios.post(`${API}/logs`, payload);
      toast.success("Log entry created successfully!");
      setForm(initialFormState);
    } catch (error) {
      console.error("Error creating log:", error);
      toast.error("Failed to create log entry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 w-full glass-effect border-b border-slate-100 px-8 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 ocean-gradient rounded-xl flex items-center justify-center">
              <FilePlus className="w-6 h-6 text-[#006994]" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                New Log Entry
              </h1>
              <p className="text-sm text-slate-500">
                {prefilledFromDD
                  ? `Adding log for ${form.dd_code}`
                  : "Record a new offline screen activity"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Sparkles className="w-4 h-4" />
            <span>All fields marked with * are required</span>
          </div>
        </div>
      </header>

      {prefilledFromDD && (
        <div className="mx-8 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <p className="text-sm text-blue-700">
            Location details have been auto-filled from <strong>{form.dd_code}</strong>. Please fill in the remaining fields.
          </p>
        </div>
      )}

      <div className="p-8 lg:p-12 max-w-4xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="dolphin-card p-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-[#006994] rounded-full"></div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Location Details
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="dolphin-label">
                  Date <span className="text-red-500">*</span>
                </Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="date-picker-trigger"
                      className={`dolphin-input justify-start text-left font-normal ${!form.date && "text-slate-400"}`}
                    >
                      <CalendarIcon className="mr-3 h-4 w-4 text-slate-400" />
                      {form.date ? format(form.date, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(date) => {
                        handleInputChange("date", date);
                        setDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  DD_Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="input-dd-code"
                  placeholder="e.g., DD_H39CLM"
                  value={form.dd_code}
                  onChange={(e) => handleInputChange("dd_code", e.target.value)}
                  className="dolphin-input"
                />
                {lookupStatus === "loading" && (
                  <p data-testid="dd-lookup-loading" className="text-xs text-slate-400 flex items-center gap-1.5">
                    <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
                    Looking up device details…
                  </p>
                )}
                {lookupStatus === "found" && (
                  <p data-testid="dd-lookup-found" className="text-xs text-green-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                    Device details auto-filled — review and edit if needed.
                  </p>
                )}
                {lookupStatus === "not_found" && (
                  <p data-testid="dd-lookup-not-found" className="text-xs text-amber-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                    No device record found — please fill in the details manually.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Venue Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="input-venue-name"
                  placeholder="e.g., D_005_00746_KIOSK"
                  value={form.venue_name}
                  onChange={(e) => handleInputChange("venue_name", e.target.value)}
                  className="dolphin-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Store name <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="input-store-name"
                  placeholder="e.g., New Awami Markaz Inc"
                  value={form.store_name}
                  onChange={(e) => handleInputChange("store_name", e.target.value)}
                  className="dolphin-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="dolphin-label">
                  Address <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  data-testid="input-address"
                  placeholder="Full street address"
                  value={form.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="dolphin-input min-h-[80px] resize-none py-3"
                />
              </div>
            </div>
          </div>

          <div className="dolphin-card p-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-[#0EA5E9] rounded-full"></div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Personnel & Equipment
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="dolphin-label">
                  PA <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="input-pa"
                  placeholder="Enter PA"
                  value={form.pa}
                  onChange={(e) => handleInputChange("pa", e.target.value)}
                  className="dolphin-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  FA
                </Label>
                <Input
                  data-testid="input-fa"
                  placeholder="Enter FA (Field Agent)"
                  value={form.fa}
                  onChange={(e) => handleInputChange("fa", e.target.value)}
                  className="dolphin-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Installer <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  testId="input-installer"
                  value={form.installer}
                  onChange={(v) => handleInputChange("installer", v)}
                  options={installerOptions}
                  placeholder="Select or type installer name"
                />
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Stick number <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="input-stick-number"
                  placeholder="Stick number"
                  value={form.stick_number}
                  onChange={(e) => handleInputChange("stick_number", e.target.value)}
                  className="dolphin-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Dolphin Manager <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  testId="input-dolphin-manager"
                  value={form.dolphin_manager}
                  onChange={(v) => handleInputChange("dolphin_manager", v)}
                  options={managerOptions}
                  placeholder="Select or type manager name"
                />
              </div>
            </div>
          </div>

          <div className="dolphin-card p-8 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-[#8B5CF6] rounded-full"></div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Network Details
              </h2>
            </div>

            <div className="space-y-4 mb-6">
              <Label className="dolphin-label">
                Network Type <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-3 gap-4">
                <button
                  type="button"
                  data-testid="network-type-wifi"
                  onClick={() => handleInputChange("network_type", "wifi")}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                    form.network_type === "wifi"
                      ? "border-[#006994] bg-[#006994]/5 text-[#006994]"
                      : "border-slate-200 hover:border-slate-300 text-slate-500"
                  }`}
                >
                  <Wifi className="w-6 h-6" />
                  <span className="font-medium">WiFi</span>
                </button>
                <button
                  type="button"
                  data-testid="network-type-ethernet"
                  onClick={() => {
                    handleInputChange("network_type", "ethernet");
                    handleInputChange("wifi_name", "");
                    handleInputChange("wifi_password", "");
                    handleInputChange("wifi_autoconnect", "");
                  }}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                    form.network_type === "ethernet"
                      ? "border-[#006994] bg-[#006994]/5 text-[#006994]"
                      : "border-slate-200 hover:border-slate-300 text-slate-500"
                  }`}
                >
                  <Cable className="w-6 h-6" />
                  <span className="font-medium">Ethernet</span>
                </button>
                <button
                  type="button"
                  data-testid="network-type-lte"
                  onClick={() => {
                    handleInputChange("network_type", "lte");
                    handleInputChange("wifi_name", "");
                    handleInputChange("wifi_password", "");
                    handleInputChange("wifi_autoconnect", "");
                  }}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 flex flex-col items-center gap-2 ${
                    form.network_type === "lte"
                      ? "border-[#006994] bg-[#006994]/5 text-[#006994]"
                      : "border-slate-200 hover:border-slate-300 text-slate-500"
                  }`}
                >
                  <Signal className="w-6 h-6" />
                  <span className="font-medium">LTE</span>
                </button>
              </div>
            </div>

            {form.network_type === "wifi" && (
              <div className="space-y-6 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                <div className="space-y-3">
                  <Label className="dolphin-label">
                    Set WiFi Autoconnect <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      data-testid="wifi-autoconnect-yes"
                      onClick={() => handleInputChange("wifi_autoconnect", "yes")}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                        form.wifi_autoconnect === "yes"
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-slate-200 hover:border-slate-300 text-slate-500"
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      data-testid="wifi-autoconnect-no"
                      onClick={() => handleInputChange("wifi_autoconnect", "no")}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                        form.wifi_autoconnect === "no"
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "border-slate-200 hover:border-slate-300 text-slate-500"
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="dolphin-label">
                      WiFi Name
                    </Label>
                    <Input
                      data-testid="input-wifi-name"
                      placeholder="Store WiFi network name"
                      value={form.wifi_name}
                      onChange={(e) => handleInputChange("wifi_name", e.target.value)}
                      className="dolphin-input bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="dolphin-label">
                      WiFi Password
                    </Label>
                    <Input
                      data-testid="input-wifi-password"
                      placeholder="Store WiFi password"
                      value={form.wifi_password}
                      onChange={(e) => handleInputChange("wifi_password", e.target.value)}
                      className="dolphin-input bg-white"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="dolphin-card p-8 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-[#8B5CF6] rounded-full"></div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Software Requirements
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="dolphin-label">
                  Latest APK <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    data-testid="latest-apk-yes"
                    onClick={() => handleInputChange("latest_apk", "yes")}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                      form.latest_apk === "yes"
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-500"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    data-testid="latest-apk-no"
                    onClick={() => handleInputChange("latest_apk", "no")}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                      form.latest_apk === "no"
                        ? "border-red-500 bg-red-50 text-red-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-500"
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="dolphin-label">
                  Set Kiosk Mode <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    data-testid="kiosk-mode-yes"
                    onClick={() => handleInputChange("kiosk_mode", "yes")}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                      form.kiosk_mode === "yes"
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-500"
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    data-testid="kiosk-mode-no"
                    onClick={() => handleInputChange("kiosk_mode", "no")}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all duration-200 ${
                      form.kiosk_mode === "no"
                        ? "border-red-500 bg-red-50 text-red-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-500"
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="dolphin-card p-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-[#10B981] rounded-full"></div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Issue Details
              </h2>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="dolphin-label">
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={form.parent_category || ""}
                    onValueChange={(value) => {
                      setForm(prev => ({
                        ...prev,
                        parent_category: value,
                        category: subcategoriesFor(value).includes(prev.category) ? prev.category : "",
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="select-parent-category" className="dolphin-input">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {CATEGORY_TREE.map((c) => (
                        <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="dolphin-label">
                    Subcategory <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={form.category}
                    onValueChange={(value) => handleInputChange("category", value)}
                    disabled={!form.parent_category}
                  >
                    <SelectTrigger data-testid="select-category" className="dolphin-input">
                      <SelectValue placeholder={form.parent_category ? "Select subcategory" : "Pick a category first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {subcategoriesFor(form.parent_category).map((sub) => (
                        <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Detailed Comments
                </Label>
                <Textarea
                  data-testid="input-comments"
                  placeholder={
                    !form.parent_category
                      ? "Select a category first…"
                      : !form.category
                      ? "Select a subcategory first…"
                      : "Add any additional details about the issue or status…"
                  }
                  value={form.detailed_comments}
                  onChange={(e) => handleInputChange("detailed_comments", e.target.value)}
                  disabled={!form.parent_category || !form.category}
                  className="dolphin-input min-h-[140px] resize-none py-3 disabled:opacity-60 disabled:cursor-not-allowed"
                />
                {(!form.parent_category || !form.category) && (
                  <p className="text-xs text-amber-600 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                    Pick Category and Subcategory above before adding comments.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="dolphin-card p-6">
            <h3 className="text-base font-semibold text-slate-900 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Owner Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="dolphin-label">
                  Owner's Name
                </Label>
                <Input
                  data-testid="input-owner-name"
                  placeholder="Enter owner's name"
                  value={form.owner_name}
                  onChange={(e) => handleInputChange("owner_name", e.target.value)}
                  className="dolphin-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Contact Number
                </Label>
                <Input
                  data-testid="input-contact-number"
                  placeholder="Enter phone number"
                  value={form.contact_number}
                  onChange={(e) => handleInputChange("contact_number", e.target.value)}
                  className="dolphin-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="dolphin-label">
                  Email Address
                </Label>
                <Input
                  data-testid="input-owner-email"
                  type="email"
                  placeholder="Enter email address"
                  value={form.owner_email}
                  onChange={(e) => handleInputChange("owner_email", e.target.value)}
                  className="dolphin-input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              data-testid="submit-log-btn"
              disabled={isSubmitting}
              className="dolphin-btn-primary h-12 px-8 text-base"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-3" strokeWidth={1.5} />
                  Submit Log Entry
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}